import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ScrollTopButton from "@/components/feature/ScrollTopButton";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import PostCard from "@/components/base/PostCard";
import usePageMeta from "@/hooks/usePageMeta";
import { posts } from "@/mocks/posts";
import { categories } from "@/mocks/categories";
import { commentSeeds } from "@/mocks/comments";
import { site } from "@/mocks/site";

interface LocalComment {
  user: string;
  content: string;
  time: string;
  likes: number;
}

function formatNum(value: number) {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}w`;
  return String(value);
}

export default function PostDetail() {
  const { id } = useParams<{ id: string }>();
  const post = posts.find((item) => item.id === id);
  const index = posts.findIndex((item) => item.id === id);

  const [liked, setLiked] = useState(false);
  const [collected, setCollected] = useState(false);
  const [draft, setDraft] = useState("");
  const [myComments, setMyComments] = useState<LocalComment[]>([]);
  const [toast, setToast] = useState("");

  const category = categories.find((item) => item.slug === post?.category);
  const related = useMemo(
    () => (post ? posts.filter((item) => item.category === post.category && item.id !== post.id).slice(0, 4) : []),
    [post],
  );
  const hotList = useMemo(() => [...posts].sort((a, b) => b.views - a.views).slice(0, 6), []);

  const comments = useMemo(() => {
    if (!post) return [];
    const start = index >= 0 ? (index * 3) % commentSeeds.length : 0;
    return Array.from({ length: 5 }, (_, offset) => commentSeeds[(start + offset) % commentSeeds.length]);
  }, [index, post]);

  const pageTitle = post ? `${post.title} - 网爆吃瓜${category?.name ?? ""}` : "内容不存在 - 网爆吃瓜";
  const pageDesc = post ? `${post.excerpt} 来自网爆吃瓜${category?.name ?? ""}频道，含吃瓜群众评论与相关推荐。` : "";

  usePageMeta({
    title: pageTitle,
    description: pageDesc,
    keywords: post ? `网爆吃瓜,${post.tags.join(",")},${category?.name ?? ""},吃瓜群众评论` : "网爆吃瓜",
    ogType: "article",
    jsonLd: post
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            datePublished: post.date,
            author: { "@type": "Person", name: post.author },
            publisher: { "@type": "Organization", name: site.brand },
            articleSection: category?.name,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "/" },
              { "@type": "ListItem", position: 2, name: category?.name, item: `/${post.category}` },
              { "@type": "ListItem", position: 3, name: post.title },
            ],
          },
        ]
      : undefined,
  });

  const submitComment = () => {
    const value = draft.trim();
    if (!value) return;
    setMyComments((list) => [
      { user: "我 · 吃瓜路人", content: value, time: "刚刚", likes: 0 },
      ...list,
    ]);
    setDraft("");
    setToast("评论已发布，谢谢捧场");
    window.setTimeout(() => setToast(""), 2400);
  };

  if (!post) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="container-page py-24 text-center">
          <span className="mx-auto w-14 h-14 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl">
            <i className="ri-emotion-unhappy-line"></i>
          </span>
          <h1 className="mt-6 text-2xl text-foreground-950">这口瓜好像被吃掉了</h1>
          <p className="mt-3 text-sm text-foreground-600">内容可能已下架或链接有误，回到首页看看今天的更新吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-home-4-line"></i>
            </span>
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const likeCount = post.likes + (liked ? 1 : 0);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <div className="bg-background-100/60 border-b border-background-200/70">
          <div className="container-page">
            <Breadcrumbs
              items={[
                { label: "首页", path: "/" },
                { label: category?.name ?? "内容", path: `/${post.category}` },
                { label: post.title },
              ]}
            />
          </div>
        </div>

        <div className="container-page py-8 md:py-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <article className="lg:col-span-8">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs whitespace-nowrap">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={category?.icon ?? "ri-fire-line"}></i>
              </span>
              {category?.name}
            </span>
            <h1 className="mt-4 text-2xl md:text-4xl text-foreground-950 leading-snug text-balance">{post.title}</h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </span>
                {post.author}
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </span>
                {post.date}
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </span>
                {formatNum(post.views)} 次围观
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-chat-3-line"></i>
                </span>
                {post.commentCount + myComments.length} 条评论
              </span>
            </div>

            <figure className="mt-8 rounded-lg overflow-hidden bg-background-100 border border-background-200/80">
              <img
                src={post.cover}
                alt={`${post.title} - 网爆吃瓜${category?.name ?? ""}`}
                title={`${post.title} - 网爆吃瓜${category?.name ?? ""}`}
                className="w-full aspect-[16/10] object-cover object-top"
              />
            </figure>

            <div className="mt-8 space-y-5">
              {post.body.map((paragraph, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-800 leading-loose">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-background-100 text-secondary-900 text-xs whitespace-nowrap border border-background-200"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setLiked((value) => !value)}
                aria-pressed={liked}
                className={`inline-flex items-center gap-2 h-11 px-5 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  liked
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-600"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={liked ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                </span>
                {formatNum(likeCount)} 个赞
              </button>
              <button
                type="button"
                onClick={() => setCollected((value) => !value)}
                aria-pressed={collected}
                className={`inline-flex items-center gap-2 h-11 px-5 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  collected
                    ? "bg-secondary-500 text-background-50"
                    : "border border-background-300 text-foreground-800 hover:border-secondary-400 hover:text-secondary-700"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={collected ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                </span>
                {collected ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setToast("链接已复制，去群里发给他们吧");
                  window.setTimeout(() => setToast(""), 2400);
                }}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-300 text-foreground-800 text-sm hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-share-forward-line"></i>
                </span>
                分享
              </button>
            </div>

            <section className="mt-12 pt-8 border-t border-background-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg md:text-xl text-foreground-950">
                  吃瓜群众评论（{comments.length + myComments.length}）
                </h2>
                <span className="text-xs text-foreground-400">按热度排序</span>
              </div>

              <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4">
                <textarea
                  value={draft}
                  maxLength={200}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder="说点什么，说不定下一条神回复就是你"
                  aria-label="发表评论"
                  rows={3}
                  className="w-full resize-none bg-transparent border-0 outline-none text-sm text-foreground-900 placeholder:text-foreground-400 leading-relaxed"
                />
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-foreground-400">{draft.length}/200</span>
                  <button
                    type="button"
                    onClick={submitComment}
                    disabled={!draft.trim()}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-send-plane-line"></i>
                    </span>
                    发布
                  </button>
                </div>
              </div>

              <ul className="mt-6 divide-y divide-background-200">
                {myComments.map((comment, i) => (
                  <li key={`mine-${i}`} className="py-4 flex gap-3">
                    <span className="w-9 h-9 shrink-0 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm">
                      {comment.user.slice(0, 1)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <span className="text-foreground-900">{comment.user}</span>
                        <span>{comment.time}</span>
                      </div>
                      <p className="mt-1.5 text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
                    </div>
                  </li>
                ))}
                {comments.map((comment) => (
                  <li key={comment.user + comment.time} className="py-4 flex gap-3">
                    <span className="w-9 h-9 shrink-0 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center text-sm">
                      {comment.user.slice(0, 1)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <span className="text-foreground-900">{comment.user}</span>
                        <span>{comment.time}</span>
                      </div>
                      <p className="mt-1.5 text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
                      <button
                        type="button"
                        className="mt-2 inline-flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-thumb-up-line"></i>
                        </span>
                        {comment.likes}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </article>

          <aside className="lg:col-span-4 flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-base text-foreground-950 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-fire-line"></i>
                </span>
                全站热榜
              </h2>
              <ol className="mt-4 space-y-3">
                {hotList.map((item, i) => (
                  <li key={item.id}>
                    <Link to={`/post/${item.id}`} className="group flex items-start gap-3 cursor-pointer">
                      <span
                        className={`w-5 h-5 shrink-0 rounded flex items-center justify-center text-xs ${
                          i < 3 ? "bg-accent-100 text-accent-800" : "bg-background-100 text-foreground-400"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </section>

            <section>
              <h2 className="text-base text-foreground-950 mb-4">相关推荐</h2>
              <ul className="flex flex-col gap-3">
                {related.map((item) => (
                  <li key={item.id}>
                    <PostCard post={item} variant="row" />
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-lg bg-foreground-950 p-5 text-background-100">
              <h2 className="text-base text-background-50 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-accent-400">
                  <i className="ri-send-plane-line"></i>
                </span>
                投稿入口
              </h2>
              <p className="mt-3 text-sm text-background-300 leading-relaxed">
                有更好笑的表情包或二创视频？投稿被采纳后会在对应频道优先展示，并标注你的署名。
              </p>
              <Link
                to="/about#submit"
                className="mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-md bg-accent-500 text-accent-950 text-sm hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                前往投稿
              </Link>
            </section>
          </aside>
        </div>
      </main>
      <SiteFooter />
      <ScrollTopButton />
      {toast ? (
        <div className="fixed left-1/2 bottom-8 -translate-x-1/2 z-50 px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm">
          {toast}
        </div>
      ) : null}
    </div>
  );
}